import {
    faClose
} from '@fortawesome/pro-regular-svg-icons/faClose';
import {
    FontAwesomeIcon
} from '@fortawesome/react-fontawesome';
import {
    Box,
    Button as MUIButton,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    type FC,
    useContext,
    useState
} from 'react';
import {
    Controller,
    FormProvider,
    useForm
} from 'react-hook-form';
import {
    toast
} from 'react-toastify';

import usePublicationSubscribePopup from '@views/OrganizationListingPage/usePublicationSubscribePopup';

import Button from '@components/typed/Button';
import InlineLoader from '@components/typed/InlineLoader';
import TextField from '@components/typed/TextField';
import Typography from '@components/typed/Typography';

import customerDataRequest from '@services/api/customer-data';

import {
    POL_THEME,
    ThemeContext
} from '@constants/theme';

import type {
    SubscribeForm,
    SubscribeToastProps
} from './types';

import IconReadings from '@assets/icons/icon-subscribe.svg';

import styles from './styles';

const SubscribeToast: FC<SubscribeToastProps> = (props) => {
    const {
        organizationDetails
    } = props;

    const themeContext = useContext(ThemeContext) as (string | undefined);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const formMethods = useForm<SubscribeForm>({
        defaultValues: {
            emailAddress: '',
            firstName: '',
            lastName: ''
        }
    });

    const {
        control,
        formState: {
            errors
        },
        handleSubmit,
        reset
    } = formMethods;

    const [
        isSubscribing,
        setIsSubscribing
    ] = useState<boolean>(false);

    const {
        hide,
        shouldShow
    } = usePublicationSubscribePopup(organizationDetails.salesforce_id, organizationDetails.is_publication_customer);

    const handleHide = () => {
        reset();
        hide();
    };

    const onSubmit = async (formData: SubscribeForm) => {
        if (!organizationDetails) {
            return;
        }

        setIsSubscribing(true);

        try {
            await customerDataRequest('post', `/organizations/${organizationDetails.salesforce_id}/subscribe/`, null, {
                email: formData.emailAddress,
                fName: formData.firstName,
                lName: formData.lastName,
                salesforceId: organizationDetails.salesforce_id,
                subscriptionSource: 'web'
            });

            toast.success('Thank you for your subscription. An email confirmation for your subscription should be sent to your inbox in the next 24 hours.');

            handleHide();
        } catch (error) {
        } finally {
            setIsSubscribing(false);
        }
    };

    const headlineCopy = themeContext === POL_THEME ? 'bulletin' : 'newsletter';

    if (!shouldShow) {
        return null;
    }

    return (
        <Box sx={styles.wrapper}>
            <MUIButton
                onClick={handleHide}
                sx={styles.close}
                variant={'contained'}
            >
                <FontAwesomeIcon
                    icon={faClose}
                    size={'2x'}
                />
            </MUIButton>
            {
                isSubscribing ? (
                    <InlineLoader />
                ) : (
                    <FormProvider {...formMethods}>
                        <Box
                            alignItems={'center'}
                            display={'flex'}
                            gap={4}
                            justifyContent={'center'}
                            maxWidth={1728}
                            width={'100%'}
                        >
                            {
                                !isMobile && (
                                    <IconReadings
                                        fill={theme.colors.plantation}
                                        height={96}
                                        stroke={theme.colors.plantation}
                                    />
                                )
                            }
                            <Box>
                                <Box sx={{
                                    pr: 3
                                }}
                                >
                                    <Typography variant={'h6'}>
                                        <b>{'Sign Up Now: '}</b>
                                        {`Subscribe to receive your weekly ${headlineCopy} from ${organizationDetails.physical_title}`}
                                    </Typography>
                                </Box>
                                <Box
                                    alignItems={isMobile ? 'normal' : 'flex-end'}
                                    display={'flex'}
                                    flexDirection={isMobile ? 'column' : 'row'}
                                    gap={2}
                                    justifyContent={'space-between'}
                                    mt={2}
                                >
                                    <Controller
                                        control={control}
                                        name={'emailAddress'}
                                        render={({
                                            field: {
                                                onBlur,
                                                onChange,
                                                value
                                            }
                                        }) => (
                                            <TextField
                                                error={!!errors.emailAddress}
                                                helperText={!!errors.emailAddress && 'Please enter a valid email address'}
                                                label={'Contact email'}
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                sx={[
                                                    ...(Array.isArray(styles.input) ? styles.input : [
                                                        styles.input
                                                    ]),
                                                    {
                                                        flex: 2
                                                    }
                                                ]}
                                                type={'email'}
                                                value={value}
                                                variant={'standard'}
                                            />
                                        )}
                                        rules={{
                                            pattern: {
                                                message: '',
                                                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
                                            },
                                            required: true
                                        }}
                                    />
                                    <Controller
                                        control={control}
                                        name={'firstName'}
                                        render={({
                                            field: {
                                                onBlur,
                                                onChange,
                                                value
                                            }
                                        }) => (
                                            <TextField
                                                error={!!errors.firstName}
                                                helperText={!!errors.firstName && 'Please enter a first name'}
                                                label={'First name'}
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                sx={styles.input}
                                                value={value}
                                                variant={'standard'}
                                            />
                                        )}
                                        rules={{
                                            required: true
                                        }}
                                    />
                                    <Controller
                                        control={control}
                                        name={'lastName'}
                                        render={({
                                            field: {
                                                onBlur,
                                                onChange,
                                                value
                                            }
                                        }) => (
                                            <TextField
                                                error={!!errors.lastName}
                                                helperText={!!errors.lastName && 'Please enter a last name'}
                                                label={'Last name'}
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                sx={styles.input}
                                                value={value}
                                                variant={'standard'}
                                            />
                                        )}
                                        rules={{
                                            required: true
                                        }}
                                    />
                                    <Button
                                        onClick={handleSubmit(onSubmit)}
                                        sx={styles.submit}
                                        variant={'contained'}
                                    >
                                        {'Subscribe'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </FormProvider>
                )
            }
        </Box>
    );
};

export default SubscribeToast;
